import React from "react";
import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { useTradeOrders } from "@/redux/selectors/ordersSelector";
import { useUI } from "@/redux/selectors/uiSelector";
import { useTheme } from "@mui/material";
import { useAccountInfo } from "@/redux/selectors/accountSelector";
//import { analyticsEnabled } from "@/utils/url";
import { TooltipedButton } from "./TooltipedButton";
import styles from "./Controls.module.scss";

interface IControlProps {
    showIntro: boolean;
    showNotifications: boolean;
    setShowIntro: (isOpen: boolean) => void;
    handleToggleNotifications: (isOpen: boolean) => void;
    showCalculator: boolean;
    handleToggleCalculator: (isOpen: boolean) => void;
    openTradesToggle: (value: boolean) => void;
    tradingHistoryToggle: (value: boolean) => void;
    pendingOrdersToggle: (value: boolean) => void;
    handleShowEconomicCalendar: () => void;
    showEconomicCalendar: boolean;
    isEconomicCalendarDataAvailabile: boolean;
}

type IGetImagePathProps = {
    key: string;
    format?: string;
};

export const Controls: React.FC<IControlProps> = ({
    showIntro,
    setShowIntro,
    showCalculator,
    showEconomicCalendar,
    handleShowEconomicCalendar,
    handleToggleCalculator,
    openTradesToggle,
    tradingHistoryToggle,
    pendingOrdersToggle,
    showNotifications,
    handleToggleNotifications,
    isEconomicCalendarDataAvailabile,
}) => {
    const { palette } = useTheme();
    const { pendingOrders, openTrades } = useTradeOrders();
    const { tradeAlerts = [], isGuest, isLazyMode } = useAccountInfo();
    const { t } = useTranslation("common");

    const noTradeAlertsNotification = (tradeAlerts && !tradeAlerts?.length) || !tradeAlerts;

    const lockedAmount = isGuest && !isLazyMode;

    const { isOpenTradesOpen, isPendingOrdersOpen, isTradingHistoryOpen, isNotificationsPopupVisible } =
        useUI();

    const getImagePath = ({ key, format = "png" }: IGetImagePathProps): string => {
        const isDarkMode = palette.mode === "dark";
        const relativePath = `/images/footer/${key}`;

        return isDarkMode ? `${relativePath}.${format}` : `${relativePath}-light-mode.${format}`;
    };

    return (
        <div
            className={clsx(styles.container, {
                [styles.isDevmode]: isEconomicCalendarDataAvailabile,
            })}>
            <TooltipedButton
                isActive={showIntro}
                setShowIsActive={setShowIntro}
                title={"platform_introduction"}
                imgUrl={getImagePath({ key: "intro", format: "svg" })}
                imgUrlHover={getImagePath({ key: "intro-hover", format: "svg" })}
            />
            <TooltipedButton
                id={`calculation${showCalculator ? "_" : ""}`}
                isActive={showCalculator}
                setShowIsActive={handleToggleCalculator}
                title={"how_to_calculate"}
                imgUrl={getImagePath({ key: "calculate", format: "svg" })}
                imgUrlHover={getImagePath({ key: "calculate-hover", format: "svg" })}
            />

            {isEconomicCalendarDataAvailabile && (
                <TooltipedButton
                    id={`economicCalendarBtn${showEconomicCalendar ? "_" : ""}`}
                    isActive={showEconomicCalendar}
                    setShowIsActive={handleShowEconomicCalendar}
                    title={t("economic_calendar")}
                    imgUrl={getImagePath({ key: "economic-calendar", format: "svg" })}
                    imgUrlHover={getImagePath({ key: "economic-calendar-hover", format: "svg" })}
                />
            )}

            <TooltipedButton
                id={`notification${isNotificationsPopupVisible ? "_" : ""}`}
                isActive={showNotifications}
                setShowIsActive={handleToggleNotifications}
                imgUrl={getImagePath({ key: "notification-bell", format: "svg" })}
                imgUrlHover={getImagePath({ key: "notification-bell-hover", format: "svg" })}
                orders={tradeAlerts}
                noDisplayingAmount={noTradeAlertsNotification}
                isLockedAmount={lockedAmount}
                title={t("tradingAlerts")}
            />

            <TooltipedButton
                isActive={isOpenTradesOpen}
                setShowIsActive={openTradesToggle}
                title={"openTrades"}
                imgUrl={getImagePath({ key: "open-trades", format: "svg" })}
                imgUrlHover={getImagePath({ key: "open-trades-hover", format: "svg" })}
                orders={openTrades}
            />
            <TooltipedButton
                isActive={isPendingOrdersOpen}
                setShowIsActive={pendingOrdersToggle}
                title={"pendingTrades"}
                imgUrl={getImagePath({ key: "pending-orders", format: "svg" })}
                imgUrlHover={getImagePath({ key: "pending-orders-hover", format: "svg" })}
                orders={pendingOrders}
            />
            <TooltipedButton
                isActive={isTradingHistoryOpen}
                setShowIsActive={tradingHistoryToggle}
                title={"tradeHistory"}
                imgUrl={getImagePath({ key: "history", format: "svg" })}
                imgUrlHover={getImagePath({ key: "history-hover", format: "svg" })}
            />
        </div>
    );
};
